import { Dialog, Divider, Drawer } from '@mui/material';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Preact from 'preact';
import { BasketItem } from './M_BasketList';
import * as BasketSnapshot from './M_BasketSnapshot';
import { RETURN_TO_STORE_HEIGHT } from './M_ReturnToStoreBanner';
import * as BasketCheckout from './O_BasketCheckout';
import { isSmallScreen } from './domain/theme/breakpoints';
import { ArModal } from './index';

export type Basket = {
    items: BasketItem[];
    total: number;
    originalTotal: number;
};

export interface Props {
    sofaSnapshot?: string;
    snapshotLoading: boolean;
    show: boolean;
    onBasketViewClose: () => void;
    basket: Basket;
    linkProps: (ArModal.ArProps | ArModal.QrProps);
    onAddToBasket?: () => void;
    onViewInRoomClick?: () => void;
}

const MobileWrapper = (props: { children: Preact.ComponentChildren }) => {
    return (
        <Box
            display="flex"
            flexDirection="column-reverse"
            height="100vh"
            minHeight="678px"
            bgcolor="#FFFFFF"
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '100%',
                    gridTemplateRows: '331px calc(100% - 331px)',
                    height: '100%',
                    width: '100%',
                }}
            >
                {props.children}
            </Box>
        </Box>
    );
};

const DesktopWrapper = (props: { children: Preact.ComponentChildren }) => {
    return (
        <Box
            display="flex"
            height="100%"
            width="fit-content"
            position="relative"
            bgcolor="#FFFFFF"
            overflow="hidden"
        >
            <Box
                sx={{
                    display: 'grid',
                    gridGap: '1px',
                    gridTemplateAreas: "'basket-view scene'",
                    gridTemplateRows: '100%',
                    gridTemplateColumns: '346px 1px 445px',
                    width: '100%',
                }}
            >
                {props.children}
            </Box>
        </Box>
    );
};

export const Component = (p: Props) => {
    const theme = useTheme();
    const isSmall = isSmallScreen(theme);

    return isSmall ? (
        <Drawer
            anchor={'bottom'}
            open={p.show}
            sx={{ maxHeight: `calc(100% - ${RETURN_TO_STORE_HEIGHT})`, height: 'max-content' }}
        >
            <MobileWrapper>
                <Box>
                    <BasketSnapshot.Component
                        sofaSnapshot={p.sofaSnapshot}
                        snapshotLoading={p.snapshotLoading}
                        onBasketViewClose={p.onBasketViewClose}
                        linkProps={p.linkProps}
                        onViewInRoomClick={p.onViewInRoomClick}
                    />
                </Box>
                <Box>
                    <BasketCheckout.Component basket={p.basket} onAddToBasket={p.onAddToBasket} />
                </Box>
            </MobileWrapper>
        </Drawer>
    ) : (
        <Dialog
            open={p.show}
            sx={{
                maxWidth: 'calc(100% - 72px)',
                width: '100%',
                height: '100%',
            }}
            BackdropProps={{
                style: {
                    borderRadius: '24px',
                    right: 0,
                    maxWidth: 'calc(100% - 72px)',
                    backgroundColor: 'rgba(34, 33, 76, 0.2)',
                },
            }}
            PaperProps={{
                sx: {
                    maxWidth: 'none !important',
                    overflow: 'hidden',
                    maxHeight: 'calc(100% - 170px)',
                    height: 'calc(100% - 170px)',
                    borderRadius: '16px',
                },
            }}
        >
            <DesktopWrapper>
                <Box>
                    <BasketCheckout.Component basket={p.basket} onAddToBasket={p.onAddToBasket} />
                </Box>
                <Box>
                    <Divider orientation="vertical" />
                </Box>
                <Box>
                    <BasketSnapshot.Component
                        sofaSnapshot={p.sofaSnapshot}
                        snapshotLoading={p.snapshotLoading}
                        onBasketViewClose={p.onBasketViewClose}
                        linkProps={p.linkProps}
                    />
                </Box>
            </DesktopWrapper>
        </Dialog>
    );
};

Component.displayName = 'BasketView';
